

import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
  FaFacebookF,
  FaInstagram,
  FaTwitter,
  FaEnvelope,
  FaLinkedin,
  FaApple,
  FaPhone,
  FaWhatsapp,
  FaRegClock,
  FaYoutube
} from "react-icons/fa";
import { SiGoogleplay } from 'react-icons/si';

const Footer = ({ companyInfo = null }) => {
  const { t } = useTranslation();
    
  // Default company information (will be overridden by companyInfo prop)
  const defaultCompanyInfo = {
    name: "Gizmore",
    logoURL: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTqT_nCUfzXEszCLgbyC9thuATp449f_uFNZg&s",
    website: "https://gizmore.in",
    supportEmail: "customercare@gizmore.in",
    supportPhone: "+91-7012345678",
    supportWhatsApp: "+917012345678",
    smFb: "https://www.facebook.com/gizmore",
    smInsta: "https://www.instagram.com/gizmore/",
    smX: "https://x.com/gizmore",
    smYt: "https://www.youtube.com/",
    smLn: "https://www.linkedin.com/"
  };

  const activeCompanyInfo = companyInfo || defaultCompanyInfo;

  const whatsappLink = activeCompanyInfo.supportWhatsApp
    ? `https://wa.me/${activeCompanyInfo.supportWhatsApp.replace(/\D/g,'')}`
    : null;

  return (
    <footer className="bg-[#071129] text-gray-300 py-12 px-6">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-10">
        {/* Contact Us */}
        <div>
          <h4 className="text-white font-semibold mb-4">{t('footer_contact_us') || 'Contact Us'}</h4>

          <div className="flex items-start space-x-3 mb-3">
            <div className="p-2 bg-green-600 rounded-full text-white">
              <FaPhone />
            </div>
            <div>
              <p className="text-sm text-gray-400">Customer Care</p>
              <a href={`tel:${activeCompanyInfo.supportPhone}`} className="text-white font-medium hover:underline">
                {activeCompanyInfo.supportPhone}
              </a>
            </div>
          </div>

          <div className="flex items-start space-x-3 mb-3">
            <div className="p-2 bg-blue-500 rounded-full text-white">
              <FaEnvelope />
            </div>
            <div>
              <p className="text-sm text-gray-400">Email Support</p>
              <a href={`mailto:${activeCompanyInfo.supportEmail}`} className="text-white font-medium hover:underline">
                {activeCompanyInfo.supportEmail}
              </a>
            </div>
          </div>

          {whatsappLink && (
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-green-500 rounded-full text-white">
                <FaWhatsapp />
              </div>
              <div>
                <p className="text-sm text-gray-400">WhatsApp Support</p>
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="text-white font-medium hover:underline">
                  {activeCompanyInfo.supportWhatsApp}
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Quick Links */}
        <div>
          <h4 className="text-white font-semibold mb-4">{t('quick_links') || 'Quick Links'}</h4>
          <ul className="space-y-3 text-gray-400">
            <li><Link to="/" className="hover:text-white">Home</Link></li>
            <li><Link to="/about" className="hover:text-white">About Us</Link></li>
            <li><Link to="/privacy-policy" className="hover:text-white">Privacy Policy</Link></li>
            <li><Link to="/help-center" className="hover:text-white">Help Center</Link></li>
            <li><Link to="/terms-of-service" className="hover:text-white">Terms of Service</Link></li>
          </ul>
        </div>

        {/* Support Hours */}
        <div>
          <h4 className="text-white font-semibold mb-4">{t('support_hours') || 'Support Hours'}</h4>
          <ul className="space-y-3 text-gray-400">
            <li className="flex items-center">
              <span className="p-2 mr-3 rounded-full text-yellow-600"><FaRegClock /></span>
              <div>
                <p className="text-sm">Mon - Fri: 9:00 AM - 6:00 PM</p>
                {/* <p className="text-white font-medium">9:00 AM - 6:00 PM</p> */}
              </div>
            </li>
            <li className="flex items-center">
              <span className="p-2 mr-3 rounded-full text-yellow-600"><FaRegClock /></span>
              <div>
                <p className="text-sm">Sat: 10:00 AM - 4:00 PM</p>
                {/* <p className="text-white font-medium">10:00 AM - 4:00 PM</p> */}
              </div>
            </li>
            <li className="flex items-center">
              <span className="p-2 mr-3 rounded-full text-gray-500"><FaRegClock /></span>
              <div>
                <p className="text-sm">Sun: Closed</p>
                {/* <p className="text-white font-medium">Closed</p> */}
              </div>
            </li>
          </ul>
        </div>

        {/* Follow Us */}
        <div>
          <h4 className="text-white font-semibold mb-4">{t('follow_us') || 'Follow Us'}</h4>
          <div className="flex items-center space-x-3 mb-3">
            {activeCompanyInfo.smFb && (
              <a href={activeCompanyInfo.smFb} target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-600 text-white">
                <FaFacebookF />
              </a>
            )}
            {activeCompanyInfo.smX && (
              <a href={activeCompanyInfo.smX} target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-800 text-white">
                <FaTwitter />
              </a>
            )}
            {activeCompanyInfo.smInsta && (
              <a href={activeCompanyInfo.smInsta} target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-tr from-pink-500 to-yellow-400 text-white">
                <FaInstagram />
              </a>
            )}
            {activeCompanyInfo.smYt && (
              <a href={activeCompanyInfo.smYt} target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full bg-red-600 text-white">
                <FaYoutube />
              </a>
            )}
            {activeCompanyInfo.smLn && (
              <a href={activeCompanyInfo.smLn} target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-700 text-white">
                <FaLinkedin />
              </a>
            )}
          </div>

          <p className="text-gray-400 text-sm">
            Stay connected for updates, tips, and exclusive offers
          </p>

          <div className="mt-6 flex space-x-3">
            <a
               href="https://apps.apple.com/in/app/prodyt/id6742870082"
               target="_blank"
               rel="noopener noreferrer"
                title={t('footer_download_app_store') || 'Download on the App Store'}
                className="inline-block"
              >
                <img class="bn46" src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"alt="bn45"/>
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.prodyt.app"
                target="_blank"
                rel="noopener noreferrer"
                title={t('footer_get_google_play') || 'Get it on Google Play'}
                className="inline-block"
              >
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                  class="bn46"
                  className="h-10 object-contain"
                />
              </a>
          </div>
        </div>
      </div>

      {/* <div className="max-w-7xl mx-auto mt-10 border-t border-gray-800 pt-6 text-center text-sm text-gray-500">
        &copy; {new Date().getFullYear()} {activeCompanyInfo.name}. {t('footer_all_rights_reserved') || 'All rights reserved.'}
      </div> */}
    </footer>
  );
};

export default Footer;